import React from "react";
import PropTypes from "prop-types";
import SkuflixFullscreenIcon from "core/icons/skuflix/SkuflixFullscreenIcon";
import SkuflixFullscreenExitIcon from "core/icons/skuflix/SkuflixFullscreenExitIcon";
import Fullscreen from "core/components/Fullscreen";
import PlayerFooterButton from "core/components/PlayerFooterButton";
import { canVideoFullscreen } from "core/utils/features";
import { useTranslation } from "react-i18next";
import analytics from "core/utils/analytics";
import { usePlayerFullscreen } from "core/components/PlayerFullscreenProvider";

export default function FullscreenButton({
  onClick,
  videoElement,
  onFullScreenChange,
  target,
  content,
  customFullscreen,
  ...props
}) {
  const { t } = useTranslation("core");
  const { isPlayerFullscreen, togglePlayerFullscreen, hasTelegramFallback } =
    usePlayerFullscreen();

  // 1. iOS логика (первый приоритет, без изменений)
  if (videoElement) {
    const vid = videoElement.ref.current;

    if (vid && vid.webkitSupportsFullscreen && canVideoFullscreen) {
      // это только для IOS
      const title = t("enterFullscreen");

      return (
        <PlayerFooterButton
          title={title}
          onClick={() => {
            analytics.event({
              category: "Player",
              action: "Fullscreen",
              label: content?.title || content?.id,
            });
            vid.webkitEnterFullscreen();
          }}
        >
          <SkuflixFullscreenIcon />
        </PlayerFooterButton>
      );
    }
  }

  // 2. Стандартная логика screenfull (второй приоритет)
  // Если screenfull поддерживается - используем его
  return (
    <Fullscreen onFullScreenChange={onFullScreenChange} target={target}>
      {({ isFullscreen, enabled, toggleFullscreen }) => {
        // Если screenfull работает - используем стандартную логику
        if (enabled) {
          const Icon = isFullscreen
            ? SkuflixFullscreenExitIcon
            : SkuflixFullscreenIcon;
          const title = isFullscreen
            ? t("exitFullscreen")
            : t("enterFullscreen");

          return (
            <PlayerFooterButton
              title={title}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (!isFullscreen) {
                  analytics.event({
                    category: "Player",
                    action: "Fullscreen",
                    label: content?.title || content?.id,
                  });
                }

                toggleFullscreen();

                if (onClick) onClick();
              }}
              {...props}
            >
              <Icon />
            </PlayerFooterButton>
          );
        }

        // 3. Telegram API-фолбек (последний приоритет)
        // Только если screenfull не работает (enabled === false)
        if (hasTelegramFallback) {
          const Icon = isPlayerFullscreen
            ? SkuflixFullscreenExitIcon
            : SkuflixFullscreenIcon;
          const title = isPlayerFullscreen
            ? t("exitFullscreen")
            : t("enterFullscreen");

          return (
            <PlayerFooterButton
              title={title}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (!isPlayerFullscreen) {
                  analytics.event({
                    category: "Player",
                    action: "Fullscreen",
                    label: content?.title || content?.id,
                  });
                }

                togglePlayerFullscreen();

                if (onClick) onClick();
              }}
              {...props}
            >
              <Icon />
            </PlayerFooterButton>
          );
        }

        // 4. SDK/кастомный фолбек (например, для iframe-плееров)
        if (customFullscreen?.enabled && customFullscreen?.toggle) {
          const Icon = customFullscreen.isFullscreen
            ? SkuflixFullscreenExitIcon
            : SkuflixFullscreenIcon;
          const title = customFullscreen.isFullscreen
            ? t("exitFullscreen")
            : t("enterFullscreen");

          return (
            <PlayerFooterButton
              title={title}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                if (!customFullscreen.isFullscreen) {
                  analytics.event({
                    category: "Player",
                    action: "Fullscreen",
                    label: content?.title || content?.id,
                  });
                }

                customFullscreen.toggle();

                if (onClick) onClick();
              }}
              {...props}
            >
              <Icon />
            </PlayerFooterButton>
          );
        }

        // Ничего не поддерживается - не показываем кнопку
        return null;
      }}
    </Fullscreen>
  );
}

FullscreenButton.propTypes = {
  target: PropTypes.string,
  onClick: PropTypes.func,
  onFullScreenChange: PropTypes.func,
  content: PropTypes.object,
  videoElement: PropTypes.shape({
    ref: PropTypes.shape({
      current: PropTypes.shape({
        webkitEnterFullscreen: PropTypes.func,
        webkitSupportsFullscreen: PropTypes.bool,
      }),
    }),
  }),
  customFullscreen: PropTypes.shape({
    enabled: PropTypes.bool,
    isFullscreen: PropTypes.bool,
    toggle: PropTypes.func,
  }),
};
